import React, { useState } from 'react';
import { generateTelegramLink, SUPPORTED_PAGES, getPageDisplayName, ExampleLinks } from '../utils/telegramLinks';

const LinkGenerator: React.FC = () => {
  const [botUsername, setBotUsername] = useState('your_bot_username');
  const [appName, setAppName] = useState('crypto_quant_app');
  const [selectedPage, setSelectedPage] = useState<string>('home');
  const [customParams, setCustomParams] = useState<Record<string, string>>({});
  const [generatedLink, setGeneratedLink] = useState<string>('');

  const generateLink = () => {
    const link = generateTelegramLink({
      botUsername,
      appName,
      page: selectedPage,
      params: customParams,
    });
    setGeneratedLink(link);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      alert('链接已复制到剪贴板！');
    } catch (err) {
      console.error('复制失败:', err);
      alert('复制失败，请手动复制');
    }
  };

  const addCustomParam = () => {
    setCustomParams({ ...customParams, '': '' });
  };

  const updateCustomParam = (index: number, key: string, value: string) => {
    const entries = Object.entries(customParams);
    entries[index] = [key, value];
    setCustomParams(Object.fromEntries(entries));
  };

  const removeCustomParam = (index: number) => {
    const entries = Object.entries(customParams);
    entries.splice(index, 1);
    setCustomParams(Object.fromEntries(entries));
  };

  return (
    <div className="min-h-screen bg-telegram-bg p-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-telegram-text mb-6">Telegram小程序链接生成器</h1>

        {/* 基本配置 */}
        <div className="bg-telegram-secondary-bg rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-telegram-text mb-4">基本配置</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-telegram-hint mb-2">Bot用户名</label>
              <input
                type="text"
                value={botUsername}
                onChange={(e) => setBotUsername(e.target.value)}
                className="w-full px-3 py-2 bg-telegram-bg border border-telegram-hint rounded-md text-telegram-text focus:outline-none focus:ring-2 focus:ring-telegram-button"
                placeholder="your_bot_username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-telegram-hint mb-2">应用名称</label>
              <input
                type="text"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                className="w-full px-3 py-2 bg-telegram-bg border border-telegram-hint rounded-md text-telegram-text focus:outline-none focus:ring-2 focus:ring-telegram-button"
                placeholder="crypto_quant_app"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-telegram-hint mb-2">目标页面</label>
              <select
                value={selectedPage}
                onChange={(e) => setSelectedPage(e.target.value)}
                className="w-full px-3 py-2 bg-telegram-bg border border-telegram-hint rounded-md text-telegram-text focus:outline-none focus:ring-2 focus:ring-telegram-button"
              >
                {SUPPORTED_PAGES.map((page) => (
                  <option key={page} value={page}>
                    {getPageDisplayName(page as any)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 自定义参数 */}
        <div className="bg-telegram-secondary-bg rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-telegram-text">自定义参数</h2>
            <button
              onClick={addCustomParam}
              className="px-3 py-1 bg-telegram-button text-white rounded-md text-sm hover:bg-telegram-button-hover"
            >
              添加参数
            </button>
          </div>

          <div className="space-y-2">
            {Object.entries(customParams).map(([key, value], index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={key}
                  onChange={(e) => updateCustomParam(index, e.target.value, value)}
                  placeholder="参数名"
                  className="flex-1 px-3 py-2 bg-telegram-bg border border-telegram-hint rounded-md text-telegram-text focus:outline-none focus:ring-2 focus:ring-telegram-button"
                />
                <input
                  type="text"
                  value={value}
                  onChange={(e) => updateCustomParam(index, key, e.target.value)}
                  placeholder="参数值"
                  className="flex-1 px-3 py-2 bg-telegram-bg border border-telegram-hint rounded-md text-telegram-text focus:outline-none focus:ring-2 focus:ring-telegram-button"
                />
                <button
                  onClick={() => removeCustomParam(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  删除
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* 生成链接 */}
        <div className="bg-telegram-secondary-bg rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-telegram-text mb-4">生成链接</h2>

          <button
            onClick={generateLink}
            className="w-full py-3 bg-telegram-button text-white rounded-md font-medium hover:bg-telegram-button-hover mb-4"
          >
            生成Telegram链接
          </button>

          {generatedLink && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-telegram-hint mb-2">生成的链接</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generatedLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-telegram-bg border border-telegram-hint rounded-md text-telegram-text text-sm"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="px-4 py-2 bg-telegram-button text-white rounded-md hover:bg-telegram-button-hover"
                  >
                    复制
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-telegram-hint mb-2">预览</label>
                <div className="p-3 bg-telegram-bg border border-telegram-hint rounded-md">
                  <a
                    href={generatedLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-telegram-link hover:underline break-all"
                  >
                    {generatedLink}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 快速链接示例 */}
        <div className="bg-telegram-secondary-bg rounded-lg p-4">
          <h2 className="text-lg font-semibold text-telegram-text mb-4">快速链接示例</h2>

          <div className="grid grid-cols-2 gap-3">
            {Object.entries(ExampleLinks.examples).map(([name, link]) => (
              <div key={name} className="p-3 bg-telegram-bg border border-telegram-hint rounded-md">
                <div className="text-sm font-medium text-telegram-text mb-1">{getPageDisplayName(name as any)}</div>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-telegram-link hover:underline break-all"
                >
                  {link}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* 使用说明 */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">使用说明</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• 在Telegram中发送生成的链接，点击即可跳转到指定页面</li>
            <li>• 可以将链接设置为Bot的菜单按钮或内联键盘</li>
            <li>• 支持自定义参数，用于传递额外的页面配置</li>
            <li>• 链接格式：https://t.me/bot_username/app_name?page=目标页面</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LinkGenerator;
